import { useState, useEffect } from 'react'
import api from '../services/api'

// Tabela Local de Escalas (TESCLOC, frota.escloc) — lista do "Local para Escala" do funcionário

interface Local { escloc: number; escdes: string; escbas: number; funcionarios?: number }

function Lista({ lista, onAdicionar, onEditar, onExcluir }: {
  lista: Local[]
  onAdicionar: () => void
  onEditar: (l: Local) => void
  onExcluir: (l: Local) => void
}) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Locais cadastrados ({lista.length})</h3>
        <button className="btn btn-primary" onClick={onAdicionar}>+ Adicionar</button>
      </div>

      {lista.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhum local cadastrado.</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {lista.map(l => (
          <div key={l.escloc} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{l.escloc} - {l.escdes || <span style={{ color: '#9ca3af', fontWeight: 400 }}>(sem descrição)</span>}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>Código Base: <b>{l.escbas}</b></span>
                <span>Funcionários: <b>{l.funcionarios ?? 0}</b></span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => onEditar(l)}>Editar</button>
              <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => onExcluir(l)}
                disabled={(l.funcionarios ?? 0) > 0} title={(l.funcionarios ?? 0) > 0 ? 'Local usado em funcionários' : undefined}>Excluir</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Formulario({ editando, inicial, onVoltar, onSalvo }: {
  editando: boolean
  inicial: Local
  onVoltar: () => void
  onSalvo: () => void
}) {
  const [form, setForm] = useState<Local>(inicial)
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function salvar() {
    if (!form.escloc || form.escloc <= 0) { setErro('Codigo do Local Invalido'); return }
    if (!form.escdes.trim()) { setErro('Descrição do Local Invalido'); return }
    setSalvando(true); setErro('')
    try {
      if (editando) await api.put(`/locais-escala/${form.escloc}`, form)
      else await api.post('/locais-escala', form)
      onSalvo()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{editando ? 'Editar Local' : 'Novo Local'}</h3>
        <button className="btn btn-outline" onClick={onVoltar}>Voltar</button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field">
          <label>Código do Local *</label>
          <input type="number" min={1} value={form.escloc || ''} disabled={editando} style={{ width: '10ch' }}
            onChange={e => setForm(f => ({ ...f, escloc: Number(e.target.value) }))} />
        </div>
        <div className="field">
          <label>Descrição *</label>
          <input value={form.escdes} maxLength={25} style={{ width: '27ch' }}
            onChange={e => setForm(f => ({ ...f, escdes: e.target.value.toUpperCase() }))} />
        </div>
        <div className="field">
          <label>Código Base</label>
          <input type="number" min={0} value={form.escbas} style={{ width: '8ch' }}
            onChange={e => setForm(f => ({ ...f, escbas: Number(e.target.value) }))} />
        </div>
      </div>

      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.75rem' }}>{erro}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Adicionar'}
        </button>
        <button className="btn btn-outline" onClick={onVoltar}>Cancelar</button>
      </div>
    </div>
  )
}

export default function LocaisEscala() {
  const [lista, setLista] = useState<Local[]>([])
  const [carregado, setCarregado] = useState(false)
  const [atual, setAtual] = useState<Local | null>(null)
  const [editando, setEditando] = useState(false)
  const [erro, setErro] = useState('')

  async function carregar() {
    const r = await api.get('/locais-escala')
    setLista(r.data)
    setCarregado(true)
  }

  useEffect(() => { carregar().catch(() => setErro('Erro ao carregar os locais.')) }, [])

  async function excluir(l: Local) {
    if (!window.confirm(`Excluir o local "${l.escloc} - ${l.escdes}"?`)) return
    setErro('')
    try {
      await api.delete(`/locais-escala/${l.escloc}`)
      await carregar()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao excluir.')
    }
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Local de Escalas
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="Tabela frota.escloc">TESCLOC</span>
      </h2>
      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erro}</div>}

      {!carregado ? null : atual ? (
        <Formulario key={atual.escloc || 'novo'} editando={editando} inicial={atual}
          onVoltar={() => setAtual(null)} onSalvo={async () => { await carregar(); setAtual(null) }} />
      ) : (
        <Lista lista={lista}
          onAdicionar={() => {
            const prox = lista.length ? Math.max(...lista.map(l => l.escloc)) + 1 : 1
            setAtual({ escloc: prox, escdes: '', escbas: 0 }); setEditando(false)
          }}
          onEditar={l => { setAtual({ ...l }); setEditando(true) }}
          onExcluir={excluir} />
      )}
    </div>
  )
}
